import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { differenceInCalendarDays, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { JobStatus } from '~/gql/graphql';
import { ScheduleEventPill } from './ScheduleEventPill';

type CalendarDayViewDialogProps = {
  date: Date;
  site: {
    id: string;
    name: string;
  };
  jobs: {
    id: string;
    summary: string;
    assignee?: {
      name: string;
      image?: string | null;
    } | null;
    scheduledStartTime?: string | null;
    scheduledEndTime?: string | null;
    start: string;
    end?: string;
    status: JobStatus;
  }[];
  showCreateJob?: boolean;
};

export default function CalendarDayViewDialog({
  date,
  site,
  jobs,
  showCreateJob = true,
}: CalendarDayViewDialogProps) {
  const { t } = useTranslation(['translation', 'nav']);
  const navigate = useNavigate();

  const handleJobClick = (job: CalendarDayViewDialogProps['jobs'][0]) => {
    if (job.status === JobStatus.Created) {
      navigate(`/jobs/calendar/${job.id}/edit`);
    } else {
      navigate(`/jobs/calendar/${job.id}`);
    }
  };

  const handleCreateJobClick = () => {
    const dateString = format(date, 'yyyy-MM-dd');
    navigate(`new?siteId=${site.id}&date=${dateString}`);
  };

  return (
    <div className='w-72 rounded-4xl border border-outline-variant bg-surface-bright px-4 pb-4'>
      <div className='flex flex-col gap-1 px-1 pb-4 pt-5 last:pb-0'>
        <h1 className='spacing text-xl font-medium leading-6'>
          {format(date, 'do MMM y')}
        </h1>
        <h2 className='text-sm font-normal text-[#3E4948]'>{site.name}</h2>
      </div>
      {jobs.length > 0 && (
        <div className='flex flex-col gap-2'>
          {jobs.map((job, index) => (
            <div
              key={job.id}
              className='group relative flex w-full hover:cursor-pointer'
              onClick={() => handleJobClick(job)}
            >
              <div className='absolute inset-y-0 right-2.5 my-auto flex h-full flex-col justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100'>
                <FontAwesomeIcon
                  className='h-4 w-4 text-secondary'
                  icon={faChevronRight}
                />
              </div>
              <div
                className='w-full duration-200 group-hover:w-[215px]'
                style={{ transitionProperty: 'width' }}
              >
                <ScheduleEventPill
                  key={index}
                  event={job}
                  height={'auto'}
                  width={'100%'}
                  absolutePosition={false}
                  inDialog={true}
                  showDates={
                    job.end !== undefined &&
                    differenceInCalendarDays(
                      new Date(job.end),
                      new Date(job.start)
                    ) > 0
                  }
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {showCreateJob && (
        <div className='flex w-full justify-start pt-4'>
          <div
            className='add-button cursor-pointer select-none'
            onClick={handleCreateJobClick}
            style={{
              // * 'add-button' class hides element until 'lg:' breakpoint
              // * which is not desired here. Inline CSS is used to override
              display: 'block',
              // * Same reasoning as above
              marginRight: 'unset',
            }}
          >
            + {t('job')}
          </div>
        </div>
      )}
    </div>
  );
}
